/**
 * @file billJobLines.js
 * @version 1.3.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-16
 * @modified 2026-10-23
 * @description A haulier's bill of JOBS, job by job (Cam, 16 Oct 2026 —
 *              Burcombe's QA03476: "each invoice row has a date, two text
 *              fields, then one or more sub lines (Transport charge, AM, Fuel
 *              surcharge) with a cost for each … each row to match with an
 *              internal doc (PO, SO, INV whatever) from the text fields, and
 *              then each sub line within the row to match with an inventory
 *              item of ours").
 *
 *   THE JOB → our document. The server works out which of our documents each
 *   job quotes (POST /api/bills/reference-links, or `reference_links` on a
 *   scan — utils/billReferenceLinks on the server: a PO-H, an order's PO-H, a
 *   bare "10183" only when customer or haulier agree, an INV- the old way…).
 *   Here: what starts chosen — an exact or likely match not already billed by
 *   another bill (`initialJobChoices`); a possible one is offered, not taken;
 *   the user can choose another candidate or "No PO" on the job's heading.
 *
 *   EACH CHARGE LINE → an EXTH item:
 *     "Fuel Surcharge", "Fuel …"          → EXTH FS (Bag) / EXTH FS (Bulk)
 *     everything else (Transport charge, AM, Tail Lift, Pallet …)
 *                                          → EXTH (Bag) / EXTH (Bulk)
 *   Bag or Bulk: from the matched document (the server's `pack` on the
 *   target — a PO-H or order carrying packed lines is Bag, only bulk tonne
 *   lines is Bulk); a job matched to nothing, or to a document of unknown
 *   weight, takes the bill's usual — the majority of its matched jobs'
 *   packs, and Bag when there is none or a tie (a haulier like Burcombe
 *   carries pallets). The account is the item's purchase account (320 Bag /
 *   321 Bulk).
 *
 *   EACH LINE'S WORDS: the charge's own words · the document's reference
 *   ("Transport charge · SO-D10187", "Fuel Surcharge · PO-H10001"); a job
 *   matched to nothing: · what it quoted as printed (INV-16137), else the
 *   haulier's own job number.
 *
 *   The user's own changes stand: a line whose item or words were changed by
 *   hand since they were set here is not set again when the job's document
 *   or pack changes (`_job.auto` remembers what was set).
 *
 *   ONLY A BILL OF JOBS (1.1.0, Cam 16 Oct 2026 — MVFS and Alta goods bills
 *   were coming in as EXTH (Bag) / (Bulk) at 320 / 321): the job logic runs
 *   only where `isBillOfJobs` says so — the reader found at least TWO jobs
 *   (distinct `job_key`s) and every line in them is a charge
 *   (utils/billLineReconcile.isChargeLine: Transport charge, AM, Fuel
 *   surcharge, Tail lift, Pallet…). A goods bill whose lines quote our POs
 *   ("SO510455702 Ref: PO-D10189", "3443851 SO") is not one: its quoted POs
 *   (exact or likely, the bill's own supplier's, not billed elsewhere —
 *   `goodsBillPoLoads`) are brought on so its lines are matched to their PO
 *   lines as Choose POs does, and nothing on its lines — item, account,
 *   words — is touched.
 *
 *   EACH LINE'S VAT (1.2.0, Cam 16 Oct 2026 — "the VAT did not get put in
 *   from the item's purchase VAT stored in inventory"): the EXTH item's
 *   purchase tax type (all four carry INPUT2), else what the bill says
 *   (`_billTax` — the line's own VAT %, or the invoice's VAT total as one
 *   rate: utils/billReadLines). A PO-H line carries no tax of its own, so the
 *   PO plays no part. A tax chosen by hand stands, as the item and words do.
 *
 *   Pure. Add / Edit Bill and Bulk Add Bills' plan use it.
 */

import { applyLinks, chosenTarget, billedWarning, NO_PO, CONFIDENCE_LABEL } from './billReferenceLinks';
import { itemCodeOf } from './customerPricing';
import { isChargeLine } from './billLineReconcile';
import { defaultTaxType } from './billReadLines';
import { getTaxRate } from './taxTypes';

export { NO_PO };

const wordsOfLine = (l) => String(l?.description ?? l?.product_name ?? '').trim();

/**
 * Is this a haulier's bill of jobs — at least two jobs, every line in them a
 * charge? Only then is the job logic (EXTH items, the job headings) applied.
 * @param {object[]} lines — the read lines (`description`) or grid lines (`product_name`)
 * @param {object[]} jobs — the server's jobs (`line_indexes` into `lines`)
 */
export function isBillOfJobs(lines, jobs) {
  const list = (jobs || []).filter(j => j && (j.line_indexes || []).length);
  if (new Set(list.map(j => String(j.job_key))).size < 2) return false;
  // A job the server matched to one of our PO-Hs (exact or likely) is a
  // haulier's job: every line in it is a charge, whatever its words (23 Oct
  // 2026 — one "Saturday AM" line took QA03714 out of the job logic). A goods
  // bill quotes PO-Ds, never a PO-H, so this does not reach one.
  const haulierLines = new Set(list.filter(isPoHJob).flatMap(j => j.line_indexes));
  const idx = [...new Set(list.flatMap(j => j.line_indexes))];
  const inJobs = idx.map(i => [i, (lines || [])[i]]).filter(([, l]) => l && wordsOfLine(l));
  return inJobs.length > 0
    && inJobs.every(([i, l]) => haulierLines.has(i) || isChargeLine(wordsOfLine(l)));
}

/** A job matched, exact or likely, to a PO-H. */
const isPoHJob = (j) => ['exact', 'likely'].includes(j?.confidence)
  && (j?.target?.po_type === 'haulage' || /^PO-H/i.test(String(j?.target?.purchase_order_number || '')));

/**
 * A goods bill quoting our POs: the PO ids to bring on, so its lines are
 * matched to their PO lines — each job's exact or likely document that is a
 * goods PO (not a PO-H, not a third-party haulage link) and not billed by
 * another bill.
 */
export function goodsBillPoLoads(jobs, { billId = null } = {}) {
  const out = [];
  for (const j of jobs || []) {
    const t = j?.target;
    if (!t || !['exact', 'likely'].includes(j.confidence)) continue;
    if (t.link !== 'goods' || t.po_type === 'haulage' || billedWarning(t, { billId })) continue;
    const id = Number(t.po_id);
    if (Number.isInteger(id) && !out.includes(id)) out.push(id);
  }
  return out;
}

/** 'fuel' for a fuel surcharge line, else 'haulage'. */
export function chargeKind(words) {
  return /\bfuel\b/i.test(String(words || '')) ? 'fuel' : 'haulage';
}

/** "EXTH FS (Bag)", "EXTH (Bulk)" … */
export function exthCode(kind, pack) {
  const p = pack === 'bulk' ? 'Bulk' : 'Bag';
  return `${kind === 'fuel' ? 'EXTH FS' : 'EXTH'} (${p})`;
}

const clean = (v) => String(v ?? '').trim();
const up = (v) => clean(v).toUpperCase();

/** The pack a chosen target says: 'bag' | 'bulk' | null. */
export const packOf = (target) => (target && (target.pack === 'bag' || target.pack === 'bulk') ? target.pack : null);

/** What starts chosen per job: exact / likely and not billed elsewhere; else nothing (possible) or No PO (none). */
export function initialJobChoices(jobs, { billId = null } = {}) {
  const out = {};
  for (const j of jobs || []) {
    const t = j.target || null;
    const takeable = t && ['exact', 'likely'].includes(j.confidence) && !billedWarning(t, { billId });
    out[j.job_key] = { po_id: takeable ? t.po_id : (j.confidence === 'none' || !t ? NO_PO : null), tick: true };
  }
  return out;
}

/**
 * The bill's usual pack: the majority of its matched jobs' packs (as the
 * user set them, else as their documents say); Bag when none is known or
 * they tie.
 */
export function billPackDefault(jobs, choices) {
  let bag = 0;
  let bulk = 0;
  for (const j of jobs || []) {
    const c = choices?.[j.job_key];
    const t = chosenTarget(j, c);
    // A matched job counts as the user set it, else as its document says.
    const p = t ? ((c?.pack === 'bag' || c?.pack === 'bulk') ? c.pack : packOf(t)) : null;
    if (p === 'bag') bag += 1;
    else if (p === 'bulk') bulk += 1;
  }
  return bulk > bag ? 'bulk' : 'bag';
}

/** A job's pack: the user's, else its document's, else the bill's usual. */
export function jobPack(job, choice, fallback = 'bag') {
  if (choice?.pack === 'bag' || choice?.pack === 'bulk') return choice.pack;
  return packOf(chosenTarget(job, choice)) || fallback;
}

/**
 * The reference a job's lines carry: the document it quoted when that is one
 * of ours (a bare "10183" as the order / PO it was read as), else the chosen
 * document's own number; matched to nothing — what it quoted as printed, else
 * the haulier's own job number.
 */
export function docRefOf(job, target) {
  const quoted = (job?.references || []).map(clean).filter(Boolean);
  if (target) {
    const ours = [target.purchase_order_number, target.order_number].map(up).filter(Boolean);
    for (const q of quoted) {
      const Q = up(q);
      if (ours.includes(Q)) return Q;
      if (/^(?:SO|PO|INV)-[A-Z]?\d+/.test(Q)) return Q;
      const bare = /^D?-?(\d{4,6})$/.exec(Q);
      if (bare) {
        const hit = [target.order_number, target.purchase_order_number].map(up).find(n => n && n.endsWith(bare[1]));
        if (hit) return hit;
      }
    }
    return up(target.order_number || target.purchase_order_number) || null;
  }
  if (quoted.length) return quoted[0];
  return clean(job?.job_key) && !/^line-\d+$/.test(clean(job.job_key)) ? clean(job.job_key) : null;
}

/** "Transport charge · SO-D10187" — the charge's words and the job's reference. */
export function jobLineWords(words, ref) {
  const w = clean(words);
  if (!ref) return w;
  return w ? `${w} · ${ref}` : ref;
}

/** An EXTH item from the item list, by code (case and spacing ignored). */
export function findExthItem(items, code) {
  const want = up(code).replace(/\s+/g, ' ');
  const hits = (items || []).filter(i => up(itemCodeOf(i)).replace(/\s+/g, ' ') === want);
  return hits.find(i => !i.is_archived) || hits[0] || null;
}

/** Bag haulage 320, bulk 321 — the EXTH items' purchase accounts, when the item is not in the list. */
const PACK_ACCOUNT = Object.freeze({ bag: '320', bulk: '321' });

/**
 * One charge line's item, account and words for its job.
 * @param {{ words: string, ref: string|null, pack: 'bag'|'bulk', items: object[] }} p
 */
export function chargeLineFields({ words, ref, pack, items, billTax = null }) {
  const code = exthCode(chargeKind(words), pack);
  const item = findExthItem(items, code);
  // The item's purchase VAT, else the bill's (utils/billReadLines).
  const tax = defaultTaxType({ itemTax: item?.purchase_tax_type ?? null, billTax });
  return {
    product_code: item ? itemCodeOf(item) : code,
    inventory_item_id: item ? item.id : null,
    account_code: (item && item.purchase_account_code) || PACK_ACCOUNT[pack] || '320',
    product_name: jobLineWords(words, ref),
    tax_type: tax.value,
    tax_from: tax.from,
  };
}

/**
 * Apply the jobs' documents and the charge lines' items to the grid's lines.
 *
 * @param {object[]} lines — grid lines
 * @param {object[]} jobs — the server's job suggestions (line_indexes into `ids`)
 * @param {object} choices — { [job_key]: { po_id, pack? } }
 * @param {string[]} ids — the grid line id of each read line
 * @param {{ items?: object[], reconciling?: boolean, only?: string[] }} [opts] — only: these jobs
 * @returns {{ lines, goodsPoIds, loadPoIds, haulagePos }} — as utils/billReferenceLinks.applyLinks
 */
export function applyJobs(lines, jobs, choices, ids, { items = [], reconciling = false, only = null } = {}) {
  const list = (jobs || []).filter(j => !only || only.includes(j.job_key));
  // Every job's document goes on (No PO included); a job left undecided keeps no PO.
  const linkChoices = {};
  for (const j of list) {
    const c = choices?.[j.job_key];
    linkChoices[j.job_key] = { po_id: c?.po_id ?? NO_PO, tick: true };
    if (linkChoices[j.job_key].po_id === null) linkChoices[j.job_key].po_id = NO_PO;
  }
  const plan = applyLinks(lines, list, linkChoices, ids, { reconciling });
  return { ...plan, lines: chargeItemsOnLines(plan.lines, jobs, choices, ids, { items, only }) };
}

/**
 * Only the charge lines' EXTH items, accounts and words, for the jobs'
 * documents as chosen — the PO links left as they are (Bulk Add Bills makes
 * only its exact PO-H links before Accept).
 */
export function chargeItemsOnLines(lines, jobs, choices, ids, { items = [], only = null } = {}) {
  const list = (jobs || []).filter(j => !only || only.includes(j.job_key));
  const fallback = billPackDefault(jobs, choices);
  const byId = new Map((lines || []).map(l => [l.id, l]));
  for (const j of list) {
    const choice = choices?.[j.job_key];
    const target = chosenTarget(j, choice);
    const pack = jobPack(j, choice, fallback);
    const ref = docRefOf(j, target);
    for (const i of j.line_indexes || []) {
      const id = ids?.[i];
      const l = id ? byId.get(id) : null;
      if (!l) continue;
      const base = l._job?.words ?? clean(l.product_name);
      const f = chargeLineFields({ words: base, ref, pack, items, billTax: l._billTax?.value ?? null });
      const auto = l._job?.auto || null;
      // A field changed by hand since it was set here stays the user's (an
      // item picked by hand brought its own VAT with it).
      const itemMine = auto && up(l.product_code) !== up(auto.product_code);
      const wordsMine = auto && clean(l.product_name) !== clean(auto.product_name);
      const taxMine = itemMine || (auto && 'tax_type' in auto && up(l.tax_type) !== up(auto.tax_type));
      const taxSet = !taxMine && f.tax_type;
      byId.set(id, {
        ...l,
        ...(itemMine ? {} : { product_code: f.product_code, inventory_item_id: f.inventory_item_id, account_code: f.account_code }),
        ...(wordsMine ? {} : { product_name: f.product_name }),
        ...(taxSet ? { tax_type: f.tax_type, tax_rate: getTaxRate(f.tax_type) || 0, _taxFrom: f.tax_from } : {}),
        // The job's heading row says which document and why.
        _hint: null,
        _job: {
          key: j.job_key, words: base, ref, pack,
          auto: {
            product_code: itemMine ? auto.product_code : f.product_code,
            product_name: wordsMine ? auto.product_name : f.product_name,
            tax_type: taxSet ? f.tax_type : (l.tax_type ?? null),
          },
        },
      });
    }
  }
  return (lines || []).map(l => byId.get(l.id) || l);
}

/** The confidence a job's choice reads as: its candidate's, else none. */
export function choiceConfidence(job, choice) {
  const t = chosenTarget(job, choice);
  return t ? t.confidence : 'none';
}

/**
 * The job heading's document picker: each candidate as "PO-H10008 · SO-D10187
 * · J Pyne & Sons" (how sure as its badge), then "No PO".
 */
export function jobDocOptions(job) {
  const opts = (job?.candidates || []).map(c => {
    const bits = [c.purchase_order_number || `PO #${c.po_id}`];
    if (c.order_number && c.order_number !== c.purchase_order_number) bits.push(c.order_number);
    if (c.customer_name) bits.push(c.customer_name);
    return {
      value: c.po_id,
      label: `${bits.join(' · ')}${c.link === 'haulage' ? ' (third-party haulage)' : ''}`,
      badge: CONFIDENCE_LABEL[c.confidence] || c.confidence,
      searchText: `${bits.join(' ')} ${c.reason || ''}`,
    };
  });
  opts.push({ value: NO_PO, label: 'No PO — nothing of ours to link' });
  return opts;
}
